import type { TFunction } from 'i18next'

import { describeProblem, fieldErrors } from '@/lib/problems'

/* Server refusals turned into the reader's sentence, for the directory.
 *
 * A module rather than a component file for quoteText.ts's reason: neither
 * function returns markup, and a component file may only export components.
 */

/** A refused delete or merge, read off the problem's structured members.
 *
 * THE NUMBERS ARE THE FEATURE. FR-39 makes the refusal name the referencing
 * quotes and cases, because "cannot delete, it is in use" sends somebody hunting
 * through every quote in the system. But Story 1.8's AC says the client maps the
 * problem's stable type suffix to a TRANSLATED message "rather than displaying
 * the English developer-facing detail" — and the two together mean the numbers
 * cannot be read out of `detail`, which is written server-side in English for a
 * log. A Russian user meeting a refused delete is the last person who should be
 * handed a second language.
 *
 * So the server puts them in `errors` — the same structured member Story 5.3's
 * branding refusal used for `issuingEntity: ['branding-incomplete']` — and this
 * reads them back. Returns null when the refusal is not this one, so a caller
 * falls through to the generic line rather than inventing a cause.
 */
export interface ReferencedRefusal {
  /** The referencing record numbers, already capped by the server. */
  references: string[]
  /** How many more were withheld by that cap. Zero when none were. */
  more: number
  /** The action the server offers instead: merge, or deactivate. */
  suggestion: 'merge' | 'deactivate'
}

export function referencedRefusal(error: unknown): ReferencedRefusal | null {
  const fields = fieldErrors(error)
  const references = fields['references']
  if (references === undefined) return null

  /* The suggestion decides which control is offered, so an unrecognised value
     must not silently become one of them: a transport entry offered a merge it
     has nothing to merge into is a dead end, and a client offered only
     deactivation loses FR-39's stated action. Unknown falls back to
     deactivation, the one action that always exists. */
  const offered = fields['suggestion']?.[0]
  const suggestion = offered === 'merge' ? 'merge' : 'deactivate'

  // A count the server sends only when it truncated. Absent means it did not,
  // which is a different fact from "it truncated by zero".
  const more = Number(fields['referencesMore']?.[0] ?? 0)

  return {
    references,
    more: Number.isFinite(more) && more > 0 ? more : 0,
    suggestion,
  }
}

/** The refused delete as one sentence, naming the records and the way out.
 *
 * Interpolated rather than concatenated, because Russian runs 15–30% longer and
 * puts the list in a different place in the clause (UX-DR-30). The catalogue owns
 * the sentence; this owns only which one and what goes in it.
 */
export function referencedMessage(
  refusal: ReferencedRefusal,
  t: TFunction,
): string {
  const list = referencesList(refusal, t)

  return refusal.suggestion === 'merge'
    ? t('directories.referenced.mergeInstead', { list })
    : t('directories.referenced.deactivateInstead', { list })
}

/** The named records of a refusal, with "and N more" when the server capped
 *  the list. */
function referencesList(refusal: ReferencedRefusal, t: TFunction): string {
  const named = refusal.references.join(', ')
  return refusal.more > 0
    ? t('directories.referenced.andMore', { named, more: refusal.more })
    : named
}

/** A directory refusal as one line, falling back to the generic mapping.
 *
 * The single entry point a panel needs: it words the FR-39 refusal when that is
 * what happened and the ordinary translated line otherwise, so no call site has
 * to know which refusals carry references.
 */
export function directoryErrorMessage(error: unknown, t: TFunction): string {
  const refusal = referencedRefusal(error)
  if (refusal) return referencedMessage(refusal, t)
  return t(describeProblem(error).detailKey)
}

/** A refused client create or edit as one line.
 *
 * One refusal here has nothing to do with what the reader typed: the number the
 * client numbering pattern would mint is already taken (409, `number:
 * ['client-number-taken']`). The form has no number field — the server mints it
 * — so the generic "conflict" line would send the reader hunting through fields
 * that are all fine. The sentence names where the fix is instead: the pattern in
 * Settings → Numbering. A stale contact list is the second (see
 * `staleContacts`). Everything else falls through to the ordinary mapping.
 */
export function clientSaveErrorMessage(error: unknown, t: TFunction): string {
  if (fieldErrors(error)['number']?.includes('client-number-taken')) {
    return t('directories.clients.numberTaken')
  }
  if (staleContacts(error)) return t('directories.contacts.stale')
  return t(describeProblem(error).detailKey)
}

/** A refused counterparty create or edit as one line — the stale contact
 *  list, or the ordinary mapping. */
export function counterpartySaveErrorMessage(error: unknown, t: TFunction): string {
  if (staleContacts(error)) return t('directories.contacts.stale')
  return t(describeProblem(error).detailKey)
}

/** A refused counterparty merge as one line (S-22b).
 *
 * Two refusals are the merge's own. A counterparty named on a debit note is
 * never merged — the notes are frozen history (AD-24) — and the server names
 * the notes through the same `references` member a refused delete uses, with
 * `suggestion: deactivate`. That sentence must not be the delete's ("cannot be
 * deleted") nor offer a merge (the act that was just refused), so it is its
 * own. And two counterparties of different categories (`category:
 * mismatched`) — the sheet offers only the source's category, so a stale list
 * is the only way to meet it. Anything else is the directory's ordinary line. */
export function counterpartyMergeErrorMessage(error: unknown, t: TFunction): string {
  const refusal = referencedRefusal(error)
  if (refusal) {
    return t('counterparties.refusals.mergeDebitNotes', { list: referencesList(refusal, t) })
  }
  if (fieldErrors(error)['category']?.includes('mismatched')) {
    return t('counterparties.refusals.categoryMismatched')
  }
  return directoryErrorMessage(error, t)
}

/** A refused client merge as one line (S-22d2).
 *
 * The client twin of `counterpartyMergeErrorMessage`'s first refusal: a client
 * billed on a debit note is never merged away — issued notes are frozen
 * history and are never moved — and the server names the notes through the
 * `references` member with `suggestion: deactivate`. The merge sheet must not
 * print the delete's sentence («удалить её нельзя») over a merge, nor offer the
 * merge that was just refused. Anything else is the directory's ordinary line. */
export function clientMergeErrorMessage(error: unknown, t: TFunction): string {
  const refusal = referencedRefusal(error)
  if (refusal) {
    return t('directories.clients.mergeDebitNotes', { list: referencesList(refusal, t) })
  }
  return directoryErrorMessage(error, t)
}

/** Whether the server refused a contact's ID (`contacts.N.id`).
 *
 * The form sends each stored contact back under the id it was served with, and
 * the server refuses an id that is not one of this record's own rows (`invalid`)
 * or that appears twice (`duplicate`). The reader typed neither: the list on
 * screen is older than the stored one — a colleague removed that contact, or
 * merged the record away under the open form. Reopening the form is the fix,
 * and the generic "check the highlighted fields" would point at fields that
 * are all fine. Any code on that key reads the same way. */
function staleContacts(error: unknown): boolean {
  return Object.keys(fieldErrors(error)).some((field) => /^contacts\.\d+\.id$/.test(field))
}
